// ============================================================================
// JARVISTRAVEL - ACCOMMODATIONS API
// Version: 1.0.0
// Accommodation (hotel/property) CRUD
// ============================================================================

import { api } from "./client";

export interface CreateAccommodationRequest {
  name: string;
  type: string;
  address?: string;
  city?: string;
  country?: string;
  check_in_date: string;
  check_out_date: string;
  check_in_time?: string;
  check_out_time?: string;
  confirmation_number?: string;
  room_type?: string;
  rating?: number;
  order_index?: number;
  contact_phone?: string;
  contact_email?: string;
  /** The property's own site. The backend reads its og:image to give the stay
   *  a photo of the actual building — see core internal/accommodations/preview.
   *  Without this field the request cannot carry one, and every property falls
   *  back to the brand gradient. */
  website?: string;
  /** A photo pasted directly. Wins over reading the website, which is what
   *  makes this worth having: the big chains answer a server-side fetch with
   *  403 regardless of who asks, so pasting is the only thing that works for
   *  them. */
  image?: string;
  notes?: string;
  /** The traveller's own cost for the whole stay, in minor units. Left out when
   *  they gave none: core then uses its own stand-in, and it refuses 0. */
  cost_minor?: number;
}

/** Where a stay's cost came from: the traveller, or core's stand-in. */
export type StayCostSource = 'traveller' | 'estimate' | 'guess';

export interface AccommodationResponse {
  id: string;
  trip_id: string;
  name: string;
  type: string;
  address?: string;
  city?: string;
  check_in_date: string;
  check_out_date: string;
  // The server returns these on the detail path (see mappers.ts); listed here so
  // the list type matches what the API actually sends (JAR-1039 review LOW-1).
  website?: string;
  image?: string;
  /** What the stay costs, in minor units of `cost_currency` (empty: the
   *  budget's currency). Absent from a core that predates the field. */
  cost_minor?: number;
  cost_currency?: string;
  cost_source?: StayCostSource;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Property Detail Types
// ---------------------------------------------------------------------------

export interface AmenityDetail {
  id: string;
  name: string;
  icon?: string;
  category?: string;
}

export interface LocationDetail {
  lat: number;
  lng: number;
  nearby?: string[];
}

/**
 * Core's own account of the property read, rather than a guess from the fields.
 *
 * - `finding`: the read is running; load the detail again shortly.
 * - `found`: the read found the property's page.
 * - `not_found`: no page was found, and the detail is the stay as entered.
 *
 * `not_found` is a deliberate answer, not a failure: core refuses to guess when
 * the typed name is a chain alone, when the page it found is not that property,
 * or when a city-plus-chain name ("Anaheim Marriott") does not match the page's
 * hotel exactly. The traveller can then give the website.
 */
export type StayLookup = 'finding' | 'found' | 'not_found';

export interface PropertyDetailResponse {
  id: string;
  trip_id: string;
  name: string;
  type: string;
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  website?: string;
  rating?: number;
  review_count: number;
  image?: string;
  /** Hotel-host addresses. Never drawn: an <img> on one tells the hotel the
   *  viewer's address. The photo comes through `image` and our copy (JAR-2254). */
  images?: string[];
  check_in?: string;
  check_out?: string;
  check_in_time?: string;
  check_out_time?: string;
  confirmation_number?: string;
  room_type?: string;
  notes?: string;
  amenities?: AmenityDetail[];
  room_service?: Record<string, unknown>;
  dining?: Record<string, unknown>[];
  services?: Record<string, unknown>[];
  location?: LocationDetail;
  /** How core's read of the hotel's pages went (JAR-2622, core#1167). Sent on
   *  this endpoint only, never on the list, and OMITTED for a stay core does
   *  not look up at all, such as a rental. An absent value therefore means
   *  "nothing to wait for", which is not the same as `not_found`. */
  lookup?: StayLookup;
  order_index: number;
  created_at: string;
  updated_at: string;
}

/**
 * Get full property detail for the property tab.
 * GET /v1/accommodations/{accommodationId}/detail
 */
export async function getPropertyDetail(accommodationId: string, signal?: AbortSignal): Promise<PropertyDetailResponse | null> {
  const result = await api.get<PropertyDetailResponse>(`/accommodations/${encodeURIComponent(accommodationId)}/detail`, true, signal);

  if (result.error) {
    return null;
  }

  return result.data;
}

/**
 * List accommodations for a trip.
 * GET /v1/trips/{trip_id}/accommodations
 */
export interface ListAccommodationsResponse {
  accommodations: AccommodationResponse[];
}

export async function listAccommodations(tripId: string, signal?: AbortSignal): Promise<AccommodationResponse[]> {
  const result = await api.get<ListAccommodationsResponse>(`/trips/${encodeURIComponent(tripId)}/accommodations`, true, signal);

  // Throw rather than flatten to []. An empty list means "this trip has no
  // stay", which is a normal state the UI answers with an Add button. A failure
  // means we could not ask — a demo session, an outage, a 401 — and answering
  // that with [] renders the same empty state and the same button, which then
  // cannot succeed. The caller's existing 'unavailable' branch was unreachable
  // for exactly this reason (JAR-1039 review, JAR-723).
  if (result.error) {
    throw result.error;
  }

  return result.data?.accommodations || [];
}

/**
 * What a change to a stay may carry.
 *
 * Every field optional, and only what changed is sent: core's
 * UpdateAccommodationRequest takes pointers and leaves anything absent alone
 * (services/trip-core/internal/accommodations/models.go), so a partial body is
 * the contract rather than a shortcut. `image` is deliberately missing, because
 * core's update does not accept one: the create does, and a stay's photo comes
 * from its website's og:image after that.
 */
export interface UpdateAccommodationRequest {
  name?: string;
  type?: string;
  address?: string;
  city?: string;
  country?: string;
  check_in_date?: string;
  check_out_date?: string;
  check_in_time?: string;
  check_out_time?: string;
  confirmation_number?: string;
  room_type?: string;
  rating?: number;
  contact_phone?: string;
  contact_email?: string;
  website?: string;
  notes?: string;
  order_index?: number;
  /** Sets the traveller's own cost. Absent leaves the cost as it is. */
  cost_minor?: number;
}

/**
 * Change a stay.
 * PUT /v1/trips/{trip_id}/accommodations/{accommodationId}
 */
export async function updateAccommodation(
  tripId: string,
  accommodationId: string,
  data: UpdateAccommodationRequest,
): Promise<AccommodationResponse> {
  const result = await api.put<AccommodationResponse>(
    `/trips/${encodeURIComponent(tripId)}/accommodations/${encodeURIComponent(accommodationId)}`,
    data,
  );

  // Thrown, not flattened, for the same reason createAccommodation throws: the
  // screen has to tell a trip that has ended from a failure worth retrying
  // (isTripLocked, JAR-1864).
  if (result.error) throw result.error;
  if (!result.data) throw new Error('Empty response from server');
  return result.data;
}

/**
 * Remove a stay.
 * DELETE /v1/trips/{trip_id}/accommodations/{accommodationId}
 */
export async function deleteAccommodation(tripId: string, accommodationId: string): Promise<void> {
  const result = await api.del<unknown>(
    `/trips/${encodeURIComponent(tripId)}/accommodations/${encodeURIComponent(accommodationId)}`,
  );
  if (result.error) throw result.error;
}

export async function createAccommodation(tripId: string, data: CreateAccommodationRequest): Promise<AccommodationResponse> {
  const result = await api.post<AccommodationResponse>(`/trips/${encodeURIComponent(tripId)}/accommodations`, data);

  if (result.error) {
    // The client's own error, so the screen can tell a trip that has ended
    // from a failure worth retrying (isTripLocked, JAR-1864).
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}
